'use client';

/**
 * SupremeAuthGate — blocks the whole app until the SDK is authenticated,
 * mirroring brief-builder's ProtectedRoute.
 *
 * - while the SDK is initialising → a full-screen loading state
 * - not authenticated → the standalone login page (SupremeLoginForm)
 * - authenticated → the app (children)
 *
 * In embedded (child-app) mode the parent SI app supplies the JWT, so the
 * gate flips straight from loading → children and the login form is never
 * shown. The login form is the standalone fallback. Source of truth lives in
 * customizations/supreme-sdk/; apply.mjs copies it into apps/web/src/supreme/.
 */

import type { ReactNode } from 'react';
import { useSupremeAuth } from './SupremeAuthProvider';
import { SupremeLoginForm } from './SupremeLoginForm';

const fullScreen: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-panel, #f4efe6)',
  color: 'var(--text-strong, #1a1a1a)',
  font: '500 14px/1.4 ui-sans-serif, system-ui, sans-serif',
  zIndex: 2147483646,
};

const spinner: React.CSSProperties = {
  width: 22,
  height: 22,
  border: '2px solid color-mix(in srgb, currentColor 25%, transparent)',
  borderTopColor: 'var(--accent, #c96442)',
  borderRadius: '50%',
  animation: 'supreme-auth-spin 0.8s linear infinite',
  marginRight: 10,
};

export function SupremeAuthGate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useSupremeAuth();

  if (isLoading) {
    return (
      <div style={fullScreen} role="status" aria-live="polite">
        <style>{'@keyframes supreme-auth-spin{to{transform:rotate(360deg)}}'}</style>
        <span style={spinner} aria-hidden="true" />
        Authenticating…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <SupremeLoginForm />;
  }

  return <>{children}</>;
}
